import type { HTMLAttributes, ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

type Slot = HTMLAttributes<HTMLElement> | Record<string, unknown>
type Slots = Record<string, Slot>

const SlotContext = createContext<Slots>({})

/**
 * Consumes slots props from parents.
 *
 * @example
 * const props =  useSlotProps(props, 'title')
 */
export function useSlotProps<T extends { slot?: string }>(props: T, defaultSlot: string): T & Slot {
    const slot = props.slot ?? defaultSlot
    const { [slot]: slotProps = {} } = useContext(SlotContext)

    return { ...props, ...slotProps }
}

/**
 * Provides slots for underlying components.
 *
 * @example
 * <SlotProvider slots={{ Title: { color: 'danger' } }}>
 *  {children}
 * </SlotProvider>
 */
export const SlotProvider = ({ slots, children }: { slots: Slots; children: ReactNode }) => {
    const parentSlots = useContext(SlotContext)
    const value = useMemo(
        () =>
            Object.fromEntries(
                [...Object.keys(parentSlots), ...Object.keys(slots)].map((current) => [
                    current,
                    {
                        ...parentSlots[current],
                        ...slots[current],
                    },
                ]),
            ),
        [parentSlots, slots],
    )

    return <SlotContext.Provider value={value}>{children}</SlotContext.Provider>
}
